import { themeVar } from '@/styles/theme';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ExportDataIcon } from '@/assets/icons/exportDataIcon';
import { FilterIcon } from '@/assets/icons/filter';
import { FilterSettingIcon } from '@/assets/icons/filterSetting';
import { RefreshIcon } from '@/assets/icons/info-icons/refresh';
import { SelectIcon } from '@/assets/icons/selectIcon';
import FilterBar, { IFilterBarData } from '@/components/basicComponents/filterBar';
import Input from '@/components/basicComponents/input';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import SelectWithCheckBox, { TData } from '../../basicComponents/selectWithCheckBox';
import ChosenFilters from './chosenFilters';
import { FilterBarWrap, FilterSettingWrap, QuickFilterSelect, QuickFilterTitle, QuickFilterWrapper, Results } from './desktop-filter';
import MoreFilters from './moreFilters';
import { AdditionalFilters, IFilterDownloadButton } from './types';

interface FilterPageProps {
  search: {
    value: string;
    onChange: any;
    placeholder: string;
  };
  primaryFilter?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
    withSearch?: boolean;
  };
  withCheckBox?: boolean;
  additionalFilters?: AdditionalFilters;
  withExportData?: boolean;
  setAllocationStatusFilter?: any;
  allocationStatusFilter?: TData[];
  currentTab?: { value: string };
  resultsValue?: number;
  refetch?: any;
  handleTableColumns?: () => void;
  filterBar?: IFilterBarData[];
  onChangeFilterBar?: (filterBarItems: IFilterBarData[]) => void;
  mobileSort?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
  };
  viewBySelect?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
  };
  downloadButton?: IFilterDownloadButton;
}

const LandscapeFilterPage = ({
  search,
  primaryFilter,
  additionalFilters,
  withExportData = false,
  withCheckBox,
  setAllocationStatusFilter,
  allocationStatusFilter,
  currentTab,
  resultsValue,
  refetch,
  handleTableColumns,
  filterBar,
  onChangeFilterBar,
  mobileSort,
  viewBySelect,
  downloadButton
}: FilterPageProps) => {
  const [moreFilter, setMoreFilter] = useState(false);
  const [chosenFilters, setChosenFilters] = useState(!!additionalFilters?.showChosenFilters);
  const [checkBoxData, setCheckBoxData] = useState<TData[]>(allocationStatusFilter ?? []);

  useEffect(() => {
    if (additionalFilters?.filters[0].typeFilter === 'Select A Filter Option' && currentTab) {
      setChosenFilters(false);
    }
  }, [currentTab?.value, additionalFilters]);

  useEffect(() => {
    if (additionalFilters?.filters[0].typeFilter !== 'Select A Filter Option') {
      setChosenFilters(true);
    }
  }, []);

  const checkBoxSelectLabel = useMemo(() => {
    const choosenNames = checkBoxData.filter((el) => el.value);
    let label = '';
    if (choosenNames.length >= 1 && choosenNames.length <= 2) {
      choosenNames.map((el, index) => {
        if (index === 0) {
          label = el.name;
        } else {
          label = label + ', ' + el.name;
        }
      });
      return label;
    } else {
      return choosenNames.length === 0 ? 'Filter by' : choosenNames.length + ' ' + 'Selected';
    }
  }, [checkBoxData]);

  const handleCheckBoxData = (id: number) => {
    const newData = [...checkBoxData];
    newData[id].value = !newData[id].value;
    setCheckBoxData(newData);
  };

  return (
    <>
      <DefaultSearchWrap>
        {/* {isResult ? (*/}
        {/*  <TopWrapper>*/}
        {/*    <Results>{additionalFilters?.amountResults} Results</Results>*/}
        {/*    <RefreshButton onClick={() => additionalFilters?.onRefetch()}>*/}
        {/*      <RefreshIcon />*/}
        {/*    </RefreshButton>*/}
        {/*  </TopWrapper>*/}
        {/* ) : null}*/}
        <MainSearchWrap>
          {downloadButton && (
            <ExportDataWrap onClick={downloadButton.onClick}>
              <ExportDataIcon fill={themeVar('font.action')} />
              <ExportDataTitle>{downloadButton?.title || 'Export Data'}</ExportDataTitle>
            </ExportDataWrap>
          )}
          <InputWrapper width={(currentTab?.value === 'pending' && withCheckBox) || primaryFilter ? '48%' : additionalFilters ? '96%' : '100%'}>
            <Input value={search.value} onChange={(e) => search.onChange(e.target.value)} placeholder={search.placeholder} size="sm" withSearchIcon />
          </InputWrapper>
          {additionalFilters && (
            <FilterWrap onClick={() => setMoreFilter(!moreFilter)} moreFilter={moreFilter}>
              <FilterIcon fill={moreFilter ? themeVar('context.light') : themeVar('font.action')} />
            </FilterWrap>
          )}
          {handleTableColumns && (
            <FilterSettingWrap onClick={handleTableColumns}>
              <FilterSettingIcon fill={themeVar('font.action')} />
            </FilterSettingWrap>
          )}
          {primaryFilter && (
            <Select
              value={primaryFilter.value}
              setValue={primaryFilter.onChange}
              options={primaryFilter.selects.map((item) => createSimpleSelectOption(item))}
              withSearch={!!primaryFilter?.withSearch}
              width="48%"
              size="sm"
            />
          )}
          {currentTab?.value === 'pending' && withCheckBox && (
            <SelectWithCheckBox
              width={'48%'}
              data={checkBoxData}
              setSelected={handleCheckBoxData}
              label={checkBoxSelectLabel}
              setAllocationStatusFilter={setAllocationStatusFilter}
              setCheckBoxData={setCheckBoxData}
            />
          )}
        </MainSearchWrap>
      </DefaultSearchWrap>
      {additionalFilters && moreFilter && (
        <MoreFilters onClose={() => setMoreFilter(false)} setChosenFilters={setChosenFilters} {...additionalFilters} />
      )}
      {additionalFilters && chosenFilters && <ChosenFilters setChosenFilters={setChosenFilters} {...additionalFilters} />}
      {filterBar && (
        <FilterBarWrap>
          <FilterBar filterBar={filterBar} onChangeFilterBar={onChangeFilterBar} />
        </FilterBarWrap>
      )}
      {mobileSort && (
        <>
          <MobileSortSelectWrapper>
            <Select
              value={mobileSort.value}
              setValue={mobileSort.onChange}
              options={mobileSort.selects.map((item) => createSimpleSelectOption(item))}
              withSearch={!!primaryFilter?.withSearch}
              label="Sort By"
              size="sm"
            />
          </MobileSortSelectWrapper>
          <TopWrapper>
            <Results>{additionalFilters?.amountResults || resultsValue || 0} Results</Results>
            <RefreshButton
              onClick={() => {
                if (additionalFilters?.onRefetch) {
                  additionalFilters.onRefetch();
                } else if (refetch) {
                  refetch();
                } else null;
              }}
            >
              <RefreshIcon fill={themeVar('action.primary')} />
            </RefreshButton>
            {viewBySelect && (
              <QuickFilterWrapper padding="0 10px 0 30px">
                <QuickFilterTitle>View By</QuickFilterTitle>
                <QuickFilterSelect
                  value={viewBySelect.value}
                  setValue={viewBySelect.onChange}
                  options={viewBySelect.selects.map((item) => createSimpleSelectOption(item))}
                  centerList={true}
                  size="sm"
                  customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                />
              </QuickFilterWrapper>
            )}
          </TopWrapper>
        </>
      )}
    </>
  );
};

const ExportDataTitle = styled.div`
  font-family: Blinker, serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  text-align: left;
  color: ${themeVar('font.action')};
`;

const ExportDataWrap = styled.div`
  display: flex;
  cursor: pointer;
  gap: 6px;
  justify-content: center;
  align-items: center;
`;

const FilterWrap = styled.div<{ moreFilter: boolean }>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ moreFilter }) => (moreFilter ? themeVar('action.active') : themeVar('layer.base'))};
  border: ${({ moreFilter }) => `1px solid ${moreFilter ? themeVar('action.active') : themeVar('font.action')}`};
  border-radius: 3px;
  padding: 10px;
`;

const DefaultSearchWrap = styled.div`
  //width: 100%;
  //display: flex;
  //align-items: flex-end;
  //justify-content: flex-end;
  //gap: 22px;
`;

const MainSearchWrap = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: 10px;
  background: ${themeVar('layer.1')};
  padding: 8px 20px;
  gap: 10px;
`;

const InputWrapper = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`;

const TopWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px;
  margin-top: 10px;
`;
const RefreshButton = styled.div`
  padding-left: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const MobileSortSelectWrapper = styled.div`
  padding: 0 20px;
`;

export default LandscapeFilterPage;
